import { useEffect } from 'react';

/**
 * Per-route <title>, description, canonical and Open Graph tags.
 *
 * The marketing site is a client-rendered SPA served by a single index.html,
 * so without this every page — Health Plans, Case Studies, all of them —
 * reported the one title baked into that file. Google renders JS and picks
 * these up; a link preview scraper does not, so this is a partial fix and the
 * full one is a prerender step at build time.
 *
 * Tags are created if absent and RESTORED on unmount, so navigating from a
 * marketing page into the app does not leave a stale description behind.
 */
export interface PageMeta {
  title: string;
  description?: string;
  /** Path only, e.g. "/case-studies". Defaults to the current path. */
  canonicalPath?: string;
  /** Absolute or root-relative image for link previews. */
  image?: string;
}

const SITE_ORIGIN = 'https://weavein.ai';

function upsert(selector: string, create: () => HTMLElement): [HTMLElement, boolean] {
  const found = document.head.querySelector<HTMLElement>(selector);
  if (found) return [found, false];
  const el = create();
  document.head.appendChild(el);
  return [el, true];
}

export default function usePageMeta({ title, description, canonicalPath, image }: PageMeta): void {
  useEffect(() => {
    const previousTitle = document.title;
    // [element, attribute, previousValue, weCreatedIt]
    const undo: Array<[HTMLElement, string, string | null, boolean]> = [];

    const setAttr = (el: HTMLElement, attr: string, value: string, created: boolean) => {
      undo.push([el, attr, el.getAttribute(attr), created]);
      el.setAttribute(attr, value);
    };

    document.title = title;

    const meta = (name: string, attr: 'name' | 'property', value: string) => {
      const [el, created] = upsert(`meta[${attr}="${name}"]`, () => {
        const m = document.createElement('meta');
        m.setAttribute(attr, name);
        return m;
      });
      setAttr(el, 'content', value, created);
    };

    if (description) {
      meta('description', 'name', description);
      meta('og:description', 'property', description);
      meta('twitter:description', 'name', description);
    }
    meta('og:title', 'property', title);
    meta('twitter:title', 'name', title);
    meta('twitter:card', 'name', 'summary_large_image');

    const path = canonicalPath ?? window.location.pathname;
    const url = `${SITE_ORIGIN}${path}`;
    meta('og:url', 'property', url);

    const [link, linkCreated] = upsert('link[rel="canonical"]', () => {
      const l = document.createElement('link');
      l.setAttribute('rel', 'canonical');
      return l;
    });
    setAttr(link, 'href', url, linkCreated);

    if (image) {
      meta('og:image', 'property', image.startsWith('http') ? image : `${SITE_ORIGIN}${image}`);
    }

    return () => {
      document.title = previousTitle;
      for (const [el, attr, previous, created] of undo) {
        if (created) {
          el.remove();
        } else if (previous === null) {
          el.removeAttribute(attr);
        } else {
          el.setAttribute(attr, previous);
        }
      }
    };
  }, [title, description, canonicalPath, image]);
}
